import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Eye, EyeOff, Shield, Key } from "lucide-react";
import { loginSecurityService, usersService } from "@/services/api";
import * as OTPAuth from "otpauth";



const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!email.endsWith("@havanagroups.com")) {
      setError("Please use your @havanagroups.com email address");
      setLoading(false);
      return;
    }

    const lockoutCheck = await loginSecurityService.checkLockout(email).catch(() => ({ locked: false }));
    if (lockoutCheck.locked) {
      setError(`Account temporarily locked. Try again in ${lockoutCheck.minutesLeft} minutes`);
      setLoading(false);
      return;
    }

    if (mfaRequired) {
      if (!mfaCode || mfaCode.length !== 6) {
        setError("Please enter your 6-digit MFA code");
        setLoading(false);
        return;
      }
      
      const userDoc = await usersService.getByEmail(email);
      if (userDoc?.mfaSecret) {
        try {
          const totp = new OTPAuth.TOTP({
            issuer: "Havana Group Ops",
            label: email,
            algorithm: "SHA1",
            digits: 6,
            period: 30,
            secret: OTPAuth.Secret.fromBase32(userDoc.mfaSecret)
          });
          
          const isValid = totp.validate({ window: 1 }) === 0;
          
          if (!isValid) {
            setError("Invalid MFA code");
            setLoading(false);
            return;
          }
        } catch (err) {
          console.error("TOTP validation error:", err);
        }
      }
    }

    const result = await login(email, password, mfaRequired ? mfaCode : null);
    if (result.success) {
      await loginSecurityService.clearFailedAttempts(email).catch(() => {});
      navigate("/");
    } else if (result.requiresMfa) {
      setMfaRequired(true);
      setLoading(false);
    } else {
      const lockoutResult = await loginSecurityService.recordFailedAttempt(email).catch(() => ({ locked: false }));
      if (lockoutResult.locked) {
        setError(`Too many failed attempts. Account locked for ${lockoutResult.minutesLeft} minutes`);
      } else {
        console.error("Login error:", result.error);
        setError(result.error || "Login failed. Check your credentials.");
      }
      setMfaRequired(false);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img 
              src="https://havanagroups.com/wp-content/uploads/2025/09/Logo-white-300x73.png" 
              alt="Havana Group Operations" 
              className="h-10"
              style={{ filter: 'brightness(0)' }}
            />
          </div>
          <CardTitle className="text-2xl text-center text-navy">Havana Group Operations</CardTitle>
          <CardDescription className="text-center">
            Sign in with your company credentials
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium text-navy">Email</label>
              <Input
                type="email"
                placeholder="you@havanagroups.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                name="email"
                autoComplete="email"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-navy">Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {mfaRequired && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-navy">MFA Code</label>
                <div className="relative">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      autoComplete="one-time-code"
                      required
                      className="flex-1"
                      maxLength={6}
                    />
                  </div>
                </div>
              </div>
            )}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
            <div className="text-center text-sm text-gray-500">
              <p>Only @havanagroups.com emails allowed</p>
              <p className="mt-1">Contact your administrator for account access</p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;